/**
 * 导购Agent - 商品接口
 *
 * 对齐后端《导购商品管理》文档。两点与项目其他接口不同，改动时注意：
 * - 分页参数是 page / page_size，不是惯用的 per_page
 * - 商品由后端同步落库，前端不手动新增，故不提供 create；
 *   update 只用于运营补充「是否主推 / 是否无效 / 是否在售 / 备注」几个字段（摘要由后端生成，页面只读）
 *
 * 商品详情接口 GET /products/{id} 返回字段与列表项完全一致，
 * 详情面板直接用列表行数据即可，无需再请求，因此这里也不封装 detail。
 */
import { DEFAULT_PAGE_SIZE } from '@/constants/api';
import { apiDownload, apiGet, apiPost, apiRequest } from '@/utils/request';
import { parsePaginatedResponse } from '@/utils/apiResponse';
import type {
  GuideProduct,
  GuideProductImportResult,
  GuideProductListParams,
  GuideProductSyncStatus,
  GuideProductSyncSubmitResult,
  GuideProductUpdatePayload,
} from '@/types/guideAgent';

export const guideProductsApi = {
  list: (params: GuideProductListParams = {}) =>
    apiGet('/products', { page_size: DEFAULT_PAGE_SIZE, ...params }).then((d) =>
      parsePaginatedResponse<GuideProduct>(d),
    ),
  /**
   * 更新运营可补充的字段（增量，未传字段保持原值）。
   *
   * timeout: 0 不限制耗时：保存后端会联动重算商品摘要，耗时不可控，
   * 走默认的 VITE_TIMEOUT 会在后端还在处理时就被前端提前中断，
   * 用户以为保存失败反复提交。
   */
  update: (id: number, data: GuideProductUpdatePayload) =>
    apiRequest<GuideProduct>(`/products/${id}`, {
      method: 'PUT',
      data,
      timeout: 0,
    }),
  /**
   * 提交手动拉取任务：按机器人从抖音侧同步商品并落库，落库后由后端调算法生成商品摘要并入向量库。
   * 恒写草稿版本（version_type=1）。
   *
   * 后端已改为**异步提交**——建任务推队列后立即返回，执行进度走 syncStatus 轮询。
   * 同一机器人已有未完成任务时不会新建，直接复用并回 already=true。
   */
  sync: (robotId: number) =>
    apiPost<GuideProductSyncSubmitResult>('/products/sync', { robot_id: robotId }),
  /** 下载主推商品导入模板（Excel 附件，文件名以响应头为准） */
  downloadTemplate: () =>
    apiDownload('/products/download-template', { filename: 'AI-主推商品导入模板.xlsx' }),
  /**
   * 主推商品导入：按 robot_id + version_type 批量更新「是否主推」。
   * 线上版本只读，只能导入草稿；表里没有的商品 ID 直接忽略，不报错。
   */
  importFeatured: (data: FormData) =>
    apiRequest<GuideProductImportResult>('/products/import', { method: 'POST', data }),
  /**
   * 导出发布规则命中的商品（(主推 或 在售) 且有效），不受列表筛选条件影响。
   * 表头与导入模板一致，改完可直接用 importFeatured 回导。
   * 后端一次性全量生成，1 万条约 8~13 秒；apiDownload 走 fetch，不受 axios 超时限制。
   */
  exportProducts: (robotId: number, versionType: number) =>
    apiDownload('/products/export', {
      params: { robot_id: robotId, version_type: versionType },
      filename: `发布商品_机器人${robotId}.xlsx`,
    }),
  /** 查询该机器人最近一次同步任务的状态与结果计数，供前端轮询 */
  syncStatus: (robotId: number) =>
    apiGet<GuideProductSyncStatus>('/products/sync-status', { robot_id: robotId }),
};
